import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableFinancialReport from '../printable/PrintableFinancialReport'; // Correct path
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Define the expected structure of the fetched report data
// (Should match the structure returned by getFinancialReportDataForPrint)
interface FinancialReportData {
  summary: {
    totalRevenue: number;
    totalPayments: number;
    totalExpenses: number;
    profit: number;
    accountsReceivable: number;
    profitMargin: number;
    pendingOrders: number;
  };
  revenueByClient: Array<{
    clientId: number;
    totalAmount: number;
    'Client.name': string;
  }>;
  expensesByCategory: Array<{
    category: string;
    totalAmount: number;
  }>;
  unpaidOrders: Array<{
    id: number;
    orderNumber: string;
    clientId: number;
    finalAmount: number;
    paidAmount: number;
    createdAt: string;
    Client: {
        name: string;
        director?: string | null;
        phone?: string | null;
    };
  }>;
  startDate: string;
  endDate: string;
}

const FinancialReportPrintPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const [reportData, setReportData] = useState<FinancialReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!startDate || !endDate) {
        setError('Не указан период для отчета.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Use the new backend endpoint for financial report print data
        const response = await axios.get<FinancialReportData>(`/reports/financial/print-data`, {
          params: { startDate, endDate }
        });
        // Add startDate and endDate to the fetched data for the printable component
        setReportData({ ...response.data, startDate, endDate });
        // Attempt to trigger print dialog after data is loaded
        setTimeout(() => {
          // window.print();
        }, 500); // Small delay
      } catch (err: any) {
        console.error('Error fetching financial report data for printing:', err);
        setError(err.response?.data?.message || 'Не удалось загрузить данные отчета для печати.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [startDate, endDate]); // Depend on query parameters

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="printable-container error-message">Ошибка: {error}</div>;
  }

  if (!reportData) {
    return <div className="printable-container">Данные для печати не найдены.</div>;
  }

  // Render the printable component with the fetched data
  return <PrintableFinancialReport reportData={reportData} />;
};

export default FinancialReportPrintPage;
